/**
 * Visual preview that renders a slot's items zipped into its assigned
 * LayoutTemplate tree — admin sees roughly how the public site will lay
 * the group out (Container/Row/Column structure with each ItemPlaceholder
 * filled by the matching preview item via DFS pre-order).
 *
 * Used inside the Preview tab of SlotGroupEditor. When no template is
 * assigned to the slot, falls back to a flat numbered list with a note —
 * still useful for spot-checking auto-mode picks.
 *
 * Carousel containers show the first item full-size + dot indicators for
 * the rest (mirrors the editor's carousel mock).
 */

import { useState } from 'react';
import { ExternalLink } from 'lucide-react';
import type {
  HydratedGroupItem,
  ItemPlaceholderNode,
  LayoutTemplateDefinition,
  TemplateContainerNode,
  TemplateNode,
} from '@news-cms/shared';
import { DEFAULT_THUMB_CLASS, HORIZONTAL_THUMB_CLASS } from '@/lib/layout-variants';

interface LayoutPreviewProps {
  /** null when the slot has no template assigned — preview falls back to
   *  a flat list with a warning notice. */
  definition: LayoutTemplateDefinition | null;
  items: HydratedGroupItem[] | undefined;
  loading: boolean;
}

/** Walk the tree DFS pre-order, return ordered placeholder ids — same
 *  ordering used by the hydrator so the public render matches preview. */
function collectPlaceholderIds(root: TemplateContainerNode): string[] {
  const ids: string[] = [];
  function walk(node: TemplateNode): void {
    if (node.type === 'item') {
      ids.push(node.id);
      return;
    }
    // Containers and Columns hold TemplateContainerChild[]; Rows hold
    // TemplateColumnNode[]. Both recursed via children — TS narrows the
    // child type per-branch, but at the walker level we just need
    // "any node with children".
    if (node.type === 'container' || node.type === 'column' || node.type === 'row') {
      (node.children as TemplateNode[]).forEach(walk);
    }
  }
  walk(root);
  return ids;
}

export function LayoutPreview({ definition, items, loading }: LayoutPreviewProps) {
  if (loading) {
    return <div className="text-xs text-slate-400">กำลังโหลด preview…</div>;
  }
  const itemList = items ?? [];

  if (!definition) {
    return (
      <div className="space-y-2">
        <div className="rounded border border-dashed border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-300">
          ⚠ Slot นี้ยังไม่ได้เลือก Layout Template — preview แสดงเป็นลิสต์เรียงลำดับธรรมดา
        </div>
        <FlatItemList items={itemList} />
      </div>
    );
  }

  const orderedIds = collectPlaceholderIds(definition.root);
  const itemByPlaceholder = new Map<string, HydratedGroupItem | undefined>();
  orderedIds.forEach((id, idx) => {
    itemByPlaceholder.set(id, itemList[idx]);
  });

  return <NodeRender node={definition.root} itemByPlaceholder={itemByPlaceholder} />;
}

function NodeRender({
  node,
  itemByPlaceholder,
}: {
  node: TemplateNode;
  itemByPlaceholder: Map<string, HydratedGroupItem | undefined>;
}) {
  if (node.type === 'item') {
    return <ItemSlot item={itemByPlaceholder.get(node.id)} variant={node.variant} />;
  }
  if (node.type === 'container') {
    if (node.variant === 'carousel') {
      return <CarouselContainer node={node} itemByPlaceholder={itemByPlaceholder} />;
    }
    return (
      <div className="flex flex-col gap-1 p-0">
        {node.children.map((c) => (
          <NodeRender key={c.id} node={c} itemByPlaceholder={itemByPlaceholder} />
        ))}
      </div>
    );
  }
  if (node.type === 'row') {
    // Bootstrap-style 12-col grid, gap-1 between columns. No padding/border
    // — matches the new live-render style from PageLiveRender. Row's own
    // configured `gap` (RowGap 0-5) is ignored here; preview standardizes
    // on gap-1 so it visually matches the page-level render.
    return (
      <div className="grid w-full grid-cols-12 gap-1 p-0">
        {node.children.map((c) => (
          <NodeRender key={c.id} node={c} itemByPlaceholder={itemByPlaceholder} />
        ))}
      </div>
    );
  }
  if (node.type === 'column') {
    return (
      <div
        style={{ gridColumn: `span ${node.span} / span ${node.span}` }}
        className="flex min-w-0 flex-col gap-1 p-0"
      >
        {node.children.map((c) => (
          <NodeRender key={c.id} node={c} itemByPlaceholder={itemByPlaceholder} />
        ))}
      </div>
    );
  }
  return null;
}

function CarouselContainer({
  node,
  itemByPlaceholder,
}: {
  node: TemplateContainerNode;
  itemByPlaceholder: Map<string, HydratedGroupItem | undefined>;
}) {
  // Carousel containers (root-only per Zod refine) hold only ItemPlaceholders.
  const itemChildren = node.children.filter((c): c is ItemPlaceholderNode => c.type === 'item');
  const [activeIdx, setActiveIdx] = useState(0);
  // Clamp in case definition shrank between renders (admin removes slides).
  const safeIdx = Math.min(activeIdx, Math.max(0, itemChildren.length - 1));
  const active = itemChildren[safeIdx];
  return (
    <div className="rounded border border-dashed border-indigo-300 bg-indigo-50/30 px-1.5 py-2 dark:border-indigo-800/60 dark:bg-indigo-950/20">
      {active && (
        <ItemSlot item={itemByPlaceholder.get(active.id)} variant={active.variant} size="hero" />
      )}
      <div className="mt-2 flex items-center justify-center gap-1.5">
        {itemChildren.map((c, i) => (
          <button
            key={c.id}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setActiveIdx(i);
            }}
            title={`Slide ${i + 1}`}
            className={`h-2 rounded-full transition ${
              i === safeIdx
                ? 'w-4 bg-indigo-500'
                : 'w-2 bg-indigo-200 hover:bg-indigo-400 dark:bg-indigo-800 dark:hover:bg-indigo-600'
            }`}
          />
        ))}
      </div>
    </div>
  );
}

/** Pick a layout shape from the variant CSS hint.
 *  Vocab (per project_layout_variant_vocab memory):
 *    - compact          → text-only (no thumb, headline list)
 *    - thumb            → horizontal (image left, text right — sidebar list)
 *    - card / featured  → vertical (image top, text below — grid cards)
 *    - hero / undefined (in carousel) → vertical (large, banner)
 *  Variant always wins over size: compact stays text-only even in a carousel. */
type ItemLayout = 'compact' | 'horizontal' | 'vertical';
function pickLayout(variant: string | undefined, size: 'normal' | 'hero'): ItemLayout {
  const v = variant?.toLowerCase();
  if (v === 'compact') return 'compact';
  // thumb (legacy) + thumb-sm/md/lg all share horizontal layout, only thumb size differs.
  if (v === 'thumb' || v === 'thumb-sm' || v === 'thumb-md' || v === 'thumb-lg') {
    return 'horizontal';
  }
  if (v === 'card' || v === 'featured' || v === 'hero') return 'vertical';
  // No variant set — fall back to size hint (carousel → vertical hero).
  return size === 'hero' ? 'vertical' : 'horizontal';
}

export function ItemSlot({
  item,
  variant,
  size = 'normal',
}: {
  item: HydratedGroupItem | undefined;
  variant?: string;
  size?: 'normal' | 'hero';
}) {
  if (!item) {
    return (
      <div className="flex h-10 items-center gap-2 rounded border border-dashed border-slate-300 px-2 text-[10px] text-slate-400 dark:border-slate-700">
        — empty —{variant && <span className="text-slate-500">[{variant}]</span>}
      </div>
    );
  }
  const layout = pickLayout(variant, size);
  const thumb = thumbOf(item);
  const link = linkOf(item);
  const isFeatured = variant?.toLowerCase() === 'featured';

  if (layout === 'compact') {
    // Text-only — headline list style. No image, even in a carousel.
    return (
      <div className="relative flex items-start gap-1.5 rounded border border-slate-200 bg-white px-2 py-1.5 dark:border-slate-700 dark:bg-slate-900">
        <div className="min-w-0 flex-1">
          <span className="line-clamp-2 text-xs font-medium leading-snug text-slate-700 dark:text-slate-200">
            {titleOf(item)}
          </span>
          <div className="text-[10px] text-slate-400">{subtitleOf(item)}</div>
        </div>
        {link && (
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 text-slate-400 hover:text-slate-600"
            title="เปิดดู"
          >
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
    );
  }

  if (layout === 'vertical') {
    // Thumbnails use 16:9 (aspect-video) — matches news industry standard
    // for hero/card thumbs and what the public Next.js renderer will use.
    // Width follows the column; height auto-derives from the ratio.
    return (
      <div className="relative overflow-hidden rounded border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
        {thumb ? (
          <img
            src={thumb}
            alt=""
            loading="lazy"
            className="aspect-video w-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).style.visibility = 'hidden';
            }}
          />
        ) : (
          <div className="aspect-video w-full bg-slate-200 dark:bg-slate-700" />
        )}
        {isFeatured && (
          <span className="absolute left-1 top-1 rounded bg-rose-500 px-1 py-0.5 text-[9px] font-medium text-white shadow">
            FEATURED
          </span>
        )}
        <div className="space-y-0.5 p-1.5">
          {/* min-h-[2lh] reserves exactly two line-heights so a 1-line
              headline occupies the same height as a 2-line one — keeps
              card titles aligned across a row/grid. line-clamp-2 caps the
              max; min-h sets the floor. */}
          <div className="line-clamp-2 min-h-[2lh] text-xs font-medium leading-snug text-slate-700 dark:text-slate-200">
            {titleOf(item)}
          </div>
          <div className="flex items-center justify-between gap-1 text-[10px] text-slate-400">
            <span className="truncate">{subtitleOf(item)}</span>
            {link && (
              <a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 text-slate-400 hover:text-slate-600"
                title="เปิดดู"
              >
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Horizontal layout — thumb / no-variant in normal context.
  // Size derived from variant: thumb-sm = h-7 w-10, thumb-md = h-10 w-16,
  // thumb-lg = h-14 w-24. Legacy `thumb` aliases to thumb-sm.
  const thumbClass = HORIZONTAL_THUMB_CLASS[variant?.toLowerCase() ?? ''] ?? DEFAULT_THUMB_CLASS;
  return (
    <div className="flex items-start gap-1.5 rounded border border-slate-200 bg-white p-1 dark:border-slate-700 dark:bg-slate-900">
      {thumb ? (
        <img
          src={thumb}
          alt=""
          loading="lazy"
          className={`shrink-0 rounded object-cover ${thumbClass}`}
          onError={(e) => {
            (e.target as HTMLImageElement).style.visibility = 'hidden';
          }}
        />
      ) : (
        <div className={`shrink-0 rounded bg-slate-200 dark:bg-slate-700 ${thumbClass}`} />
      )}
      <div className="min-w-0 flex-1">
        <span className="line-clamp-1 text-xs font-medium text-slate-700 dark:text-slate-200">
          {titleOf(item)}
        </span>
        <div className="text-[10px] text-slate-400">{subtitleOf(item)}</div>
      </div>
      {link && (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className="text-slate-400 hover:text-slate-600"
          title="เปิดดู"
        >
          <ExternalLink className="h-3 w-3" />
        </a>
      )}
    </div>
  );
}

function FlatItemList({ items }: { items: HydratedGroupItem[] }) {
  if (items.length === 0) {
    return <div className="text-xs text-slate-400">— ยังไม่มี item —</div>;
  }
  return (
    <ol className="space-y-1 text-xs">
      {items.map((it, idx) => (
        <li
          key={`${it.type}-${idx}`}
          className="flex items-start gap-2 rounded bg-white p-1 dark:bg-slate-900"
        >
          <span className="w-5 shrink-0 text-center tabular-nums text-slate-400">{idx + 1}</span>
          <div className="min-w-0 flex-1">
            <ItemSlot item={it} />
          </div>
        </li>
      ))}
    </ol>
  );
}

function thumbOf(it: HydratedGroupItem): string | null {
  if (it.type === 'article') return it.coverImageUrl;
  if (it.type === 'video') return it.thumbnailUrl;
  if (it.type === 'url') return it.imageUrl;
  return null; // streaming — no thumbnail
}
function titleOf(it: HydratedGroupItem): string {
  // Streaming items carry only a URL; use it as the visible label.
  if (it.type === 'streaming') return it.url;
  return it.title;
}
function subtitleOf(it: HydratedGroupItem): string {
  if (it.type === 'article') return it.categoryName ?? '—';
  if (it.type === 'video') return it.channelTitle ?? '—';
  if (it.type === 'streaming') return 'live stream';
  return it.url;
}
function linkOf(it: HydratedGroupItem): string | null {
  if (it.type === 'article') return `/posts/${it.id}`;
  if (it.type === 'video') return `https://www.youtube.com/watch?v=${it.youtubeId}`;
  return it.url;
}
